import { Circle, interaction, SizableText, Stack, XStack } from '@mythical/ui'
import { LandingHero as LandingHeroBlockType } from 'app/blocks/landing/landing-hero'
import { Container } from 'app/components/structure/Container'
import { ButtonLink } from 'app/components/primitive/Button'
import Spinner from 'app/components/primitive/Spinner'
import { MarketingHeading } from 'app/features/landing/components'
import { VideoBlock } from '../Video'
import { useVideoPlayerControls } from 'app/components/primitive/VideoPlayer/useVideoPlayerControls'
import { IconSpeakerHigh } from 'app/components/primitive/Icon'
import { useStores } from 'app/store'

interface LandingHeroBlockProps {
  block: LandingHeroBlockType
  videoRefreshing?: boolean
}

export const LandingHeroBlock = ({
  block,
  videoRefreshing,
}: LandingHeroBlockProps) => {
  const { mainTitle, mainSubtitle, heroVideo } = block
  const { playerRef, controls, isMuted } = useVideoPlayerControls()
  const { userStore } = useStores()
  const canMemberfulLink = userStore?.canMemberfulLink ?? false

  return (
    <Container variant="full" gap="$5" $gtSm={{ gap: '$8.5' }} mb="$-5" paddedX>
      <Container
        variant="mediumLarge"
        jc="center"
        ai="center"
        gap="$3.5"
        $gtSm={{ gap: '$5' }}
      >
        <MarketingHeading tag="h1">{mainTitle}</MarketingHeading>

        <SizableText ff="$title" size="$m" ta="center" $gtSm={{ size: '$xl' }}>
          {mainSubtitle}
        </SizableText>

        {canMemberfulLink && heroVideo?.ctaLabel && (
          <XStack jc="center">
            <ButtonLink href="/perks">{heroVideo?.ctaLabel}</ButtonLink>
          </XStack>
        )}
      </Container>

      {heroVideo?.video?.[0] && (
        <Stack position="relative" $platform-web={{ maxHeight: '85vh' }}>
          {videoRefreshing ? (
            <Stack
              aspectRatio={16 / 9}
              flex={1}
              borderRadius="$3"
              overflow="hidden"
              ai="center"
              jc="center"
              backgroundColor="$black"
            >
              <Spinner size="large" />
            </Stack>
          ) : (
            <>
              <VideoBlock
                block={heroVideo?.video?.[0]}
                thumbnail={heroVideo.thumbnail?.sizes?.thumbnail?.url}
                isUnlocked
                autoplay
                muted
                playerRef={playerRef}
              />

              {isMuted && (
                <Circle
                  onPress={controls.toggleMute}
                  bg="$bgBrand"
                  size={64}
                  rotateZ="15deg"
                  cursor="pointer"
                  bottom="35%"
                  right="10%"
                  position="absolute"
                  zIndex={1000}
                  $gtSm={{
                    size: 128,
                    bottom: '15%',
                    right: '15%',
                  }}
                  {...interaction.opacity}
                >
                  <IconSpeakerHigh w={20} h={20} $gtSm={{ w: 28, h: 28 }} />
                  <SizableText
                    ff="$condensed"
                    size="$2"
                    $gtSm={{ size: '$6.5' }}
                    tt="uppercase"
                  >
                    Sound on
                  </SizableText>
                </Circle>
              )}
            </>
          )}
        </Stack>
      )}
    </Container>
  )
}
